import { useState, useEffect, useRef } from 'react';
import { fetchUserPrefs, saveUserPrefs } from '../../../utils/userPrefs';

/**
 * useTablePersist
 *
 * Persiste las preferencias de la tabla en base de datos y localStorage.
 *
 * Flujo:
 *   1. Al montar (o cuando llegan columnas), carga prefs desde DB.
 *   2. Mientras se cargan / justo después, `readyToSave` = false → el guardado NO corre.
 *   3. Un `setTimeout` de 300ms tras completar la carga pone `readyToSave` = true.
 *   4. A partir de ese momento, cualquier cambio de estado (usuario) dispara el guardado.
 */
export const useTablePersist = ({
  storageKey: storageKeyProp,
  persistEnabled,
  persistKey,
  persistVista,
  desktopBaseKeys,
  initialColumnOrder,
  normalizedUsuarioId,
  normalizedEmpresaId,
  hiddenFields = [],
  defaultHiddenCols = [],
  defaultRowsPerPage = 25
}) => {
  // ─── Clave localStorage ─────────────────────────────────────────────────────
  const storageKey = storageKeyProp
    || (persistEnabled && persistKey && persistVista
      ? `table_prefs__${persistKey}__${persistVista}${normalizedUsuarioId ? `__u${normalizedUsuarioId}` : ''}${normalizedEmpresaId ? `__e${normalizedEmpresaId}` : ''}`
      : null);

  const loadLocalPrefs = () => {
    if (!storageKey || typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch (e) { }
    return null;
  };

  const initialPrefs = loadLocalPrefs();

  // ─── Estados ─────────────────────────────────────────────────────────────────
  const [isLoaded, setIsLoaded] = useState(false);
  // readyToSave: solo true después de que la carga de DB terminó Y pasó el debounce
  const [readyToSave, setReadyToSave] = useState(false);

  const [hiddenCols, setHiddenCols] = useState(() => {
    const baseHidden = new Set([...hiddenFields]);
    if (initialPrefs?.hiddenCols && Array.isArray(initialPrefs.hiddenCols)) {
      initialPrefs.hiddenCols.forEach(k => baseHidden.add(k));
    }
    const unhidden = new Set(initialPrefs?.explicitlyUnhiddenCols || []);
    (defaultHiddenCols || []).forEach(k => {
      if (!unhidden.has(k)) {
        baseHidden.add(k);
      }
    });
    return baseHidden;
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const initialKeys = initialColumnOrder || [];
    const currentKeys = [
      ...initialKeys,
      ...desktopBaseKeys.filter(k => !initialKeys.includes(k)),
    ];
    if (initialPrefs?.columnOrder && Array.isArray(initialPrefs.columnOrder)) {
      if (currentKeys.length > 0) {
        const merged = initialPrefs.columnOrder.filter(k => currentKeys.includes(k));
        currentKeys.forEach(k => { if (!merged.includes(k)) merged.push(k); });
        return merged;
      }
      return initialPrefs.columnOrder;
    }
    if (initialColumnOrder && Array.isArray(initialColumnOrder)) {
      const merged = [...initialColumnOrder];
      currentKeys.forEach(k => { if (!merged.includes(k)) merged.push(k); });
      return merged;
    }
    return desktopBaseKeys;
  });

  const [pinnedCols, setPinnedCols] = useState(() => {
    if (initialPrefs?.pinnedCols && Array.isArray(initialPrefs.pinnedCols)) {
      return new Set(initialPrefs.pinnedCols);
    }
    return new Set([]);
  });

  const [sortConfig, setSortConfig] = useState(() => {
    if (initialPrefs?.sortConfig) return initialPrefs.sortConfig;
    return { field: null, direction: 'asc' };
  });

  const [columnFilters, setColumnFilters] = useState(() => ({}));
  const [globalQuery, setGlobalQuery] = useState(() => '');

  const [rowsPerPage, setRowsPerPage] = useState(() => {
    if (initialPrefs?.rowsPerPage && Number.isFinite(initialPrefs.rowsPerPage)) {
      return initialPrefs.rowsPerPage;
    }
    return defaultRowsPerPage || 25;
  });

  // ─── Refs ────────────────────────────────────────────────────────────────────
  const isMounted = useRef(true);
  const dbLoaded = useRef(false);   // ¿ya hicimos al menos una carga de DB?
  const lastSavedPrefsRef = useRef(null); // Evitar re-guardar en DB si no hubo cambios reales

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ─── 1. CARGAR DESDE BASE DE DATOS ───────────────────────────────────────────
  useEffect(() => {
    if (!persistEnabled || !persistKey) {
      setIsLoaded(true);
      setReadyToSave(true);
      return;
    }

    // Si ya cargamos de la BD, no recargar repetidamente por cambios en columnas
    if (dbLoaded.current) return;

    // Cada vez que recargamos de DB, desactivamos el guardado
    setReadyToSave(false);

    const loadFromDB = async () => {
      try {
        const rawResponse = await fetchUserPrefs({ modulo: persistKey, vista: persistVista });
        if (!isMounted.current) return;

        if (rawResponse) {
          let dbPrefs = null;
          try {
            dbPrefs = typeof rawResponse === 'string' ? JSON.parse(rawResponse) : rawResponse;
          } catch (e) {
            console.error('[useTablePersist] Error parseando JSON de DB', e);
          }

          if (dbPrefs && typeof dbPrefs === 'object') {
            const unhidden = new Set(dbPrefs.explicitlyUnhiddenCols || []);
            const combinedHidden = new Set([...(dbPrefs.hiddenCols || []), ...hiddenFields]);
            (defaultHiddenCols || []).forEach(k => {
              if (!unhidden.has(k)) {
                combinedHidden.add(k);
              }
            });
            setHiddenCols(combinedHidden);

            if (dbPrefs.columnOrder && Array.isArray(dbPrefs.columnOrder)) {
              const savedOrder = dbPrefs.columnOrder;
              const initialKeys = initialColumnOrder || [];
              const currentKeys = [
                ...initialKeys,
                ...desktopBaseKeys.filter(k => !initialKeys.includes(k)),
              ];
              let mergedOrder = currentKeys.length > 0
                ? savedOrder.filter(k => currentKeys.includes(k))
                : savedOrder;
              currentKeys.forEach(k => { if (!mergedOrder.includes(k)) mergedOrder.push(k); });
              setColumnOrder(mergedOrder);
            }
            if (dbPrefs.pinnedCols) setPinnedCols(new Set(dbPrefs.pinnedCols));
            if (dbPrefs.sortConfig) setSortConfig(dbPrefs.sortConfig);
            if (dbPrefs.rowsPerPage && Number.isFinite(Number(dbPrefs.rowsPerPage))) {
              setRowsPerPage(Number(dbPrefs.rowsPerPage));
            }

            // Registrar como últimas preferencias guardadas para no disparar POST inmediato
            const currentUnhidden = (defaultHiddenCols || []).filter(k => !combinedHidden.has(k));
            lastSavedPrefsRef.current = JSON.stringify({
              hiddenCols: Array.from(combinedHidden),
              explicitlyUnhiddenCols: currentUnhidden,
              columnOrder: dbPrefs.columnOrder || [],
              pinnedCols: Array.from(dbPrefs.pinnedCols || []),
              sortConfig: dbPrefs.sortConfig || { field: null, direction: 'asc' },
              rowsPerPage: dbPrefs.rowsPerPage || 10
            });
          }
        }
      } catch (e) {
        console.error('[useTablePersist] Error cargando preferencias desde DB:', e);
      } finally {
        if (isMounted.current) {
          dbLoaded.current = true;
          setIsLoaded(true);
          // Dar tiempo a React para que aplique todos los setState de arriba
          // antes de habilitar el guardado
          setTimeout(() => {
            if (isMounted.current) setReadyToSave(true);
          }, 400);
        }
      }
    };

    loadFromDB();
  }, [persistEnabled, persistKey, persistVista, desktopBaseKeys.length]);

  // Guardado instantáneo en localStorage como fallback
  useEffect(() => {
    if (!isLoaded || !storageKey) return;
    try {
      const explicitlyUnhiddenCols = (defaultHiddenCols || []).filter(k => !hiddenCols.has(k));
      const currentPrefs = {
        hiddenCols: Array.from(hiddenCols),
        explicitlyUnhiddenCols,
        columnOrder,
        pinnedCols: Array.from(pinnedCols),
        sortConfig,
        rowsPerPage
      };
      localStorage.setItem(storageKey, JSON.stringify(currentPrefs));
    } catch (e) {}
  }, [isLoaded, hiddenCols, columnOrder, pinnedCols, sortConfig, rowsPerPage, storageKey, defaultHiddenCols]);

  // ─── 2. GUARDAR EN DB (Debounced 300ms) ───────────────────────────────────────
  useEffect(() => {
    if (!readyToSave || !persistEnabled || !persistKey) return;

    const explicitlyUnhiddenCols = (defaultHiddenCols || []).filter(k => !hiddenCols.has(k));
    const currentPrefs = {
      hiddenCols: Array.from(hiddenCols),
      explicitlyUnhiddenCols,
      columnOrder,
      pinnedCols: Array.from(pinnedCols),
      sortConfig,
      rowsPerPage
    };
    const currentPrefsStr = JSON.stringify(currentPrefs);

    // Evitar guardar si es idéntico a lo que cargamos de la BD o ya guardamos
    if (lastSavedPrefsRef.current === currentPrefsStr) {
      return;
    }

    const timer = setTimeout(async () => {
      if (storageKey) {
        localStorage.setItem(storageKey, currentPrefsStr);
      }

      try {
        await saveUserPrefs({
          modulo: persistKey,
          vista: persistVista,
          pref: currentPrefs
        });
        lastSavedPrefsRef.current = currentPrefsStr;
      } catch (e) {
        console.error('[useTablePersist] Error guardando preferencias en DB:', e);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [readyToSave, persistEnabled, persistKey, persistVista, hiddenCols, columnOrder, pinnedCols, sortConfig, rowsPerPage, storageKey, defaultHiddenCols]);

  // ─── 3. Sincronizar columnas nuevas cuando llegan los datos ──────────────────
  useEffect(() => {
    if (desktopBaseKeys.length > 0) {
      setColumnOrder(prev => {
        const newCols = desktopBaseKeys.filter(k => !prev.includes(k));
        if (newCols.length > 0) return [...prev, ...newCols];
        return prev;
      });
      setHiddenCols(prev => {
        const missingHiddenFields = hiddenFields.filter(f => !prev.has(f));
        const unhidden = new Set(initialPrefs?.explicitlyUnhiddenCols || []);
        const missingDefaultHidden = (defaultHiddenCols || []).filter(f => !prev.has(f) && !unhidden.has(f));
        const toAdd = [...missingHiddenFields, ...missingDefaultHidden];
        if (toAdd.length > 0) return new Set([...Array.from(prev), ...toAdd]);
        return prev;
      });
    }
  }, [desktopBaseKeys, hiddenFields, defaultHiddenCols]);

  return {
    isLoaded,
    hiddenCols, setHiddenCols,
    columnOrder, setColumnOrder,
    pinnedCols, setPinnedCols,
    sortConfig, setSortConfig,
    columnFilters, setColumnFilters,
    globalQuery, setGlobalQuery,
    rowsPerPage, setRowsPerPage
  };
};
